import { PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Loading } from '@/components/ui/daisy';
import useFormList from '@/data/hook/shared/useFormList';
import Exam from '@/models/Exam';
import ExamArea from '@/models/ExamArea';

import ExamBlockInlineForm, { ExamBlockFormFields } from './ExamBlockInlineForm';

type ExamBlockFormListProps = {
  exam: Exam;
  examAreas: ExamArea[];
  onSubmit: (data: ExamBlockFormFields) => Promise<unknown>;
  isLoading: boolean;
  disabled?: boolean;
};

export default function ExamBlockFormList({
  exam,
  examAreas,
  onSubmit,
  isLoading,
  disabled,
}: ExamBlockFormListProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examWorkspace.questions',
  });
  const { openForms, addForm, removeForm, handleFormSubmit } =
    useFormList<ExamBlockFormFields>({ onSubmit });

  const isCreateDisabled = disabled || openForms.length > 0;

  return (
    <div className="flex flex-col gap-6">
      <ConditionalRenderer condition={isLoading}>
        <FieldsetContainer className="flex justify-center items-center py-5">
          <Loading variant="spinner" size="md" color="primary" />
        </FieldsetContainer>
      </ConditionalRenderer>
      {openForms.map(formId => (
        <ExamBlockInlineForm
          key={formId}
          exam={exam}
          examAreas={examAreas}
          onSubmit={handleFormSubmit(formId)}
          onCancel={() => removeForm(formId)}
          isLoading={isLoading}
        />
      ))}
      <FieldsetContainer>
        <Button
          size="md"
          onClick={addForm}
          className="bg-base-300"
          disabled={isCreateDisabled}
        >
          <PlusIcon className="w-4 h-4" />
          {t('createBlock')}
        </Button>
      </FieldsetContainer>
    </div>
  );
}
